import React, { useState } from "react";
import Select from "react-select";
import { Modal } from "react-bootstrap";
import toast from "react-hot-toast";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import { ACTION_ICONS } from "../../config/siteConfig";
import DetailView from "../location/DetailView";
import { banksService } from "../../services/banks.service";
import { SOMETHING_WENT_WRONG } from "../../constants/strings";
import { dropdownSelectStyles } from "../../utils/selectStyles";
import { Badge, DETAIL_ACCENTS, Pill, detailTableWrap, detailTdStyle, detailThStyle, show, titleCase } from "../common/DetailUI";

const ACCOUNT_TYPE_OPTIONS = [
  { value: "savings", label: "Savings" },
  { value: "current", label: "Current" },
];

const labelStyle = { fontWeight: 600, fontSize: 12.5, color: "#334155", marginBottom: 6 };
const inputStyle = { borderRadius: 8, border: "1px solid #c3cad6", fontSize: 13, height: 40, background: "#fff" };

// same type as the field values above on the profile page
const cellStyle = { ...detailTdStyle, fontSize: 13.5, fontWeight: 600, color: "#0f172a" };

const actionBtn = (bg, color) => ({ width: 32, height: 32, borderRadius: 8, background: bg, color, border: "none", padding: 0 });

// Bank accounts of the logged-in user with View / Edit actions.
// onUpdated(bank) is called with the refreshed record after a successful update.
const ProfileBanks = ({ banks = [], onUpdated }) => {
  const [viewBank, setViewBank] = useState(null);
  const [editBank, setEditBank] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const openEdit = (b) => {
    setForm({
      account_holder_name: b.account_holder_name || "",
      bank_name: b.bank_name || "",
      branch_name: b.branch_name || "",
      account_number: b.account_number || "",
      account_type: b.account_type || "savings",
      ifsc_code: b.ifsc_code || "",
      is_primary: !!b.is_primary,
    });
    setEditBank(b);
  };

  const closeEdit = () => {
    if (!saving) setEditBank(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: name === "ifsc_code" ? value.toUpperCase() : value });
  };

  const validate = () => {
    if (!form.account_holder_name.trim()) return "Account holder name is required";
    if (!form.bank_name.trim()) return "Bank name is required";
    if (!form.account_number.trim()) return "Account number is required";
    if (!/^\d{6,20}$/.test(form.account_number.trim())) return "Enter a valid account number (6 to 20 digits)";
    if (!form.ifsc_code.trim()) return "IFSC code is required";
    if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(form.ifsc_code.trim())) return "Enter a valid IFSC code";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    const confirm = await withReactContent(Swal).fire({
      title: "Update bank details?",
      text: "The changes will be saved to this bank account.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, update",
      cancelButtonText: "Cancel",
    });
    if (!confirm.isConfirmed) return;
    try {
      setSaving(true);
      const res = await banksService.update(editBank.id, {
        account_holder_name: form.account_holder_name.trim(),
        bank_name: form.bank_name.trim(),
        branch_name: form.branch_name.trim(),
        account_number: form.account_number.trim(),
        account_type: form.account_type,
        ifsc_code: form.ifsc_code.trim(),
        is_primary: form.is_primary,
      });
      if (res?.success) {
        await withReactContent(Swal).fire({ title: "Success", text: res.message, icon: "success" });
        const fresh = await banksService.details(editBank.id);
        onUpdated?.(fresh?.success ? fresh.resData : { ...editBank, ...form }, form.is_primary);
        setEditBank(null);
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      withReactContent(Swal).fire({ title: "Error", text: SOMETHING_WENT_WRONG, icon: "error" });
    } finally {
      setSaving(false);
    }
  };

  const field = (text, required, node, cls = "col-12 col-md-6") => (
    <div className={cls}>
      <div style={labelStyle}>
        {text}
        {required && <span className="text-danger"> *</span>}
      </div>
      {node}
    </div>
  );

  const textInput = (name, placeholder, extra) => (
    <input
      type="text"
      className="form-control"
      name={name}
      placeholder={placeholder}
      value={form[name] ?? ""}
      onChange={handleChange}
      style={inputStyle}
      {...extra}
    />
  );

  if (banks.length === 0) return <div style={{ fontSize: 13, color: "#94a3b8" }}>No bank accounts</div>;

  return (
    <>
      <div className="table-responsive" style={detailTableWrap}>
        <table className="table table-sm align-middle mb-0">
          <thead>
            <tr>
              {["Bank", "Account Holder", "Account No.", "IFSC", "Type", "Status", "Action"].map((h) => (
                <th key={h} style={detailThStyle}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {banks.map((b) => (
              <tr key={b.id}>
                <td style={cellStyle}>
                  {show(b.bank_name)} {b.is_primary && <Pill value="Primary" accent={DETAIL_ACCENTS.pink} />}
                  <div style={{ fontSize: 11, color: "#94a3b8" }}>{b.branch_name}</div>
                </td>
                <td style={cellStyle}>{show(b.account_holder_name)}</td>
                <td style={cellStyle}>{show(b.account_number)}</td>
                <td style={cellStyle}>{show(b.ifsc_code)}</td>
                <td style={cellStyle}>{titleCase(b.account_type)}</td>
                {/* <td style={cellStyle}>{show(b.upi_id)}</td> */}
                <td style={cellStyle}><Badge value={b.status} /></td>
                <td style={cellStyle}>
                  <div className="d-flex align-items-center gap-2">
                    <button
                      type="button"
                      className="btn btn-sm d-flex align-items-center justify-content-center"
                      title="View"
                      onClick={() => setViewBank(b)}
                      style={actionBtn("#e6f7f1", "#0f8a5f")}
                    >
                      <i className={`bi ${ACTION_ICONS.view}`} data-action="view" style={{ fontSize: 13 }}></i>
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm d-flex align-items-center justify-content-center"
                      title="Edit"
                      onClick={() => openEdit(b)}
                      style={actionBtn("#e7f1ff", "#1971c2")}
                    >
                      <i className={`bi ${ACTION_ICONS.edit}`} data-action="edit" style={{ fontSize: 13 }}></i>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal size="lg" centered show={!!editBank} onHide={closeEdit}>
        <form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700 }}>Update Bank Details</div>
              <div style={{ fontSize: 12, color: "rgba(255,255,255,.78)" }}>Update bank account information</div>
            </div>
          </Modal.Header>
          <Modal.Body>
            <div className="row g-3">
              {field("Account Holder Name", true, textInput("account_holder_name", "Enter account holder name"))}
              {field("Bank Name", true, textInput("bank_name", "Enter bank name"))}
              {field("Branch Name", false, textInput("branch_name", "Enter branch name"))}
              {field("Account Number", true, textInput("account_number", "Enter account number", { inputMode: "numeric" }))}
              {field(
                "Account Type",
                true,
                <Select
                  options={ACCOUNT_TYPE_OPTIONS}
                  value={ACCOUNT_TYPE_OPTIONS.find((o) => o.value === form.account_type) || null}
                  onChange={(o) => setForm({ ...form, account_type: o ? o.value : "savings" })}
                  menuPortalTarget={document.body}
                  styles={dropdownSelectStyles}
                />,
              )}
              {field("IFSC Code", true, textInput("ifsc_code", "e.g. HDFC0001234", { maxLength: 11 }))}
              {field(
                "Primary Account",
                false,
                <div className="form-check form-switch mt-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="profile_bank_primary"
                    checked={!!form.is_primary}
                    onChange={(e) => setForm({ ...form, is_primary: e.target.checked })}
                  />
                  <label className="form-check-label" htmlFor="profile_bank_primary">Use as primary account</label>
                </div>,
              )}
            </div>
          </Modal.Body>
          <Modal.Footer className="justify-content-end gap-2">
            <button
              type="button"
              className="btn"
              onClick={closeEdit}
              disabled={saving}
              style={{ background: "#fff", border: "1px solid #c3cad6", borderRadius: 8, height: 40, fontSize: 13, color: "#444" }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn text-white fw-semibold"
              disabled={saving}
              style={{ background: "var(--btn-bg)", border: "none", borderRadius: 8, height: 40, fontSize: 13 }}
            >
              {saving ? "Saving..." : "Update Bank Details"}
            </button>
          </Modal.Footer>
        </form>
      </Modal>

      <Modal size="lg" centered show={!!viewBank} onHide={() => setViewBank(null)}>
        <Modal.Header
          closeButton
          closeVariant="white"
          style={{ background: "linear-gradient(135deg, var(--btn-bg) 0%, var(--btn-hover-bg) 100%)", borderBottom: "none" }}
        >
          <Modal.Title className="d-flex align-items-center gap-2" style={{ color: "#fff", fontSize: 18, fontWeight: 600 }}>
            <i className={`bi ${ACTION_ICONS.view}`} data-action="view"></i> Bank Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: 24, background: "#f8f9fe" }}>
          {viewBank && (
            <DetailView
              fields={[
                { label: "Account Holder", value: viewBank.account_holder_name },
                { label: "Bank Name", value: viewBank.bank_name },
                { label: "Branch", value: viewBank.branch_name },
                { label: "Account Number", value: viewBank.account_number },
                { label: "Account Type", value: viewBank.account_type },
                { label: "IFSC Code", value: viewBank.ifsc_code },
                { label: "MICR Code", value: viewBank.micr_code },
                { label: "SWIFT Code", value: viewBank.swift_code },
                { label: "UPI ID", value: viewBank.upi_id },
                { label: "PAN Number", value: viewBank.pan_number },
                { label: "Bank Address", value: viewBank.bank_address, full: true },
                { label: "City", value: viewBank.city },
                { label: "State", value: viewBank.state },
                { label: "Pincode", value: viewBank.pincode },
                { label: "Primary", value: viewBank.is_primary ? "Yes" : "No" },
                { label: "Status", value: viewBank.status, badge: true },
                {
                  label: "Verified On",
                  value: viewBank.verification_date ? String(viewBank.verification_date).replace("T", " ").slice(0, 19) : null,
                },
              ]}
            />
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default ProfileBanks;
